// Flattens the group tree (groupAndSummarize.js) depth-first into autotable body rows,
// tagging each row's metadata (rowType/level) in a parallel `rowMeta` array (indexed the
// same as `body`) so pdfHooksV2.js's didParseCell/didDrawCell can style each row without
// re-deriving structure from the rendered table. N-level grouping recurses naturally —
// 1, 2, or 5 levels use the same code path.
export function flattenLeafColumns(columns) {
    const result = [];
    columns.forEach((col) => {
        if (col.visible === false) return;
        if (col.columns) {
            result.push(...flattenLeafColumns(col.columns));
        } else {
            result.push(col);
        }
    });
    return result;
}

const INDENT_PER_LEVEL = '    '; // 4 spaces per nesting level, rendered as plain text indent

export function buildBody(tree, leafColumns, groupByFields, grandTotal, { formatValue }) {
    const body = [];
    const rowMeta = [];
    const columnCount = leafColumns.length;

    function summaryRow(summary, fallbackLabel) {
        const row = leafColumns.map((col) => {
            const value = summary ? summary[col.dataField] : undefined;
            return value === undefined ? '' : formatValue({ value, column: col });
        });
        if (row.length && !row[0]) row[0] = fallbackLabel;
        return row;
    }

    function pushGroupHeader(node, level) {
        const indent = INDENT_PER_LEVEL.repeat(level);
        const label = `${indent}${node.key === undefined || node.key === null ? '' : node.key}`;
        body.push([{ content: label, colSpan: columnCount }]);
        rowMeta.push({ rowType: 'groupHeader', level });
    }

    function pushGroupFooter(node, level) {
        body.push(summaryRow(node.groupSummary, 'Total'));
        rowMeta.push({ rowType: 'groupFooter', level });
    }

    function pushDataRow(row) {
        body.push(leafColumns.map((col) => {
            const formatted = formatValue({ value: row[col.dataField], column: col });
            // TEMP DEBUG — remove once the "status" columns' rendering is confirmed correct.
            if (col.cellTemplate && String(col.cellTemplate).toLowerCase().includes('status')) {
                // eslint-disable-next-line no-console
                console.log('[pdf-export-v2 DEBUG] status column:', {
                    dataField: col.dataField,
                    caption: col.caption,
                    cellTemplate: col.cellTemplate,
                    rawValue: row[col.dataField],
                    formattedValue: formatted,
                    row,
                });
            }
            return formatted;
        }));
        rowMeta.push({ rowType: 'data', level: 0 });
    }

    // Only the innermost (leaf) group level gets a "Total" subtotal row — a parent group's own
    // subtotal would just repeat/redundantly sum its children's subtotals, so it's skipped
    // entirely rather than stacking a "Total" per nesting level.
    function walk(node, level) {
        node.children.forEach((child) => {
            pushGroupHeader(child, level);
            if (child.children) {
                walk(child, level + 1);
            } else {
                child.rows.forEach(pushDataRow);
                pushGroupFooter(child, level);
            }
        });
    }

    if (!groupByFields || !groupByFields.length) {
        tree.rows.forEach(pushDataRow);
    } else {
        walk(tree, 0);
    }

    if (grandTotal && Object.keys(grandTotal).length) {
        body.push(summaryRow(grandTotal, 'Grand Total'));
        rowMeta.push({ rowType: 'grandTotal', level: 0 });
    }

    return { body, rowMeta };
}
